import '@angular/compiler';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  Inject,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  RulesActions,
  RulesConditions,
  RulesTag,
  Step,
  findConditionByNameAndValue,
} from '@app/features/automation-rules/utils';
import { SettingsService } from '@app/services/settings/settings.service';
import { Store } from '@ngrx/store';
import { OrderStatus, ReturnPreferences } from '@pdqdev/pdq-shared/dist/entities';
import { CountryCode } from '@pdqdev/pdq-shared/dist/utils/Country-utils';
import { Subscription } from 'rxjs';
import { AutomationRulesActions } from '../../../actions';
import { AutomationRulesReducer } from '../../../reducers';
import {
  RuleTemplateSelectorComponent,
  SelectedRuleParams,
} from '../../rule-template/rule-template-selector.component';

interface DialogData {
  step: Step;
  ruleId: number;
  selectedRuleParams: SelectedRuleParams;
  internalName: string;
}

@Component({
  selector: 'automation-rule-edit-dialog',
  templateUrl: './automation-rule-edit-dialog.html',
  styleUrls: ['./automation-rule-edit-dialog.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AutomationRuleEditDialog implements AfterViewInit, OnInit, OnDestroy {
  componentRef: ComponentRef<any>;

  step: Step;
  ruleId: number = null;
  selectedRuleParams: SelectedRuleParams;
  selectedConditions: AutomationRulesActions.SelectedCondition[][] = [[]];
  selectedAction: AutomationRulesActions.SelectedAction;
  templateData = null;
  templateData$ = this.store.select(AutomationRulesReducer.selectTemplateData);
  templateDataSubscription$: Subscription;
  notVerifiedName;
  internalName: UntypedFormControl;

  @ViewChild('target', { read: ViewContainerRef, static: true })
  vcRef: ViewContainerRef;
  displayedMenuItems = [
    {
      text: Step.verify,
    },
    {
      text: Step.assign,
    },
    {
      text: Step.fulfill,
    },
    {
      text: Step.track,
    },
  ];
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<AutomationRuleEditDialog>,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    this.templateDataSubscription$ = this.templateData$.subscribe(
      (templateData?: AutomationRulesReducer.TemplateData) => {
        if (templateData) {
          this.templateData = {
            parameters: templateData.parameters,
            template: templateData['ng-template'],
            notVerifiedName: templateData.notVerifiedName,
          };
        }
      },
    );
    this.initDialogData();
    this.createComponent();

    this.componentRef.instance.onChangeConditionSelect.subscribe(({ value, index }) => {
      const row = index[0];
      const column = index[1];
      if (this.selectedConditions.length > row) {
        if (this.selectedConditions[row].length > column) {
          this.selectedConditions[row][column] = value;
        } else {
          this.selectedConditions[row].push(value);
        }
      } else {
        this.selectedConditions[row] = [value];
      }
    });
    this.componentRef.instance.onChangeActionSelect.subscribe((value) => (this.selectedAction = value));
    this.componentRef.instance.onChangeNotVerifiedName?.subscribe((value) => {
      this.notVerifiedName = value;
    });
    this.componentRef.instance.onChangeOperatorSelect.subscribe(({ operator, is, index }) => {
      const row = index[0];
      const column = index[1];

      const value = { ...this.selectedConditions[row][column].value };
      if (typeof is !== 'undefined') {
        value.is = is;
      }
      value.operator = operator;
      this.selectedConditions[row][column].value = value;
    });
    this.componentRef.instance.onChangeOperator2Select.subscribe(({ operator, is, index }) => {
      const row = index[0];
      const column = index[1];

      const value = { ...this.selectedConditions[row][column].value };
      if (typeof is !== 'undefined') {
        value.is = is;
      }
      value.operator2 = operator;
      this.selectedConditions[row][column].value = value;
    });

    this.componentRef.instance.onRemoveCondition.subscribe(({ index }) => {
      const row = index[0];
      const column = index[1];
      if (this.selectedConditions[row].length === 1) {
        this.selectedConditions.splice(row, 1);
      } else {
        this.selectedConditions[row].splice(column, 1);
      }
    });
    if (this.templateData) {
      this.showTemplateComponent(this.templateData);
    }
  }

  ngAfterViewInit() {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
    this.templateDataSubscription$?.unsubscribe();
  }

  initDialogData() {
    if (!this.data) {
      return;
    }
    this.step = this.data.step;
    this.ruleId = this.data.ruleId;
    this.selectedRuleParams = this.data.selectedRuleParams;

    this.internalName = new UntypedFormControl(this.data.internalName ?? '');

    for (let j = 0; j < this.selectedRuleParams.orWhen.length; j++) {
      for (let i = 0; i < this.selectedRuleParams.orWhen[j].andWhen.length; i++) {
        const andWhenName: string = Object.keys(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValue: any = Object.values(this.selectedRuleParams.orWhen[j].andWhen[i])?.pop();
        const andWhenValueToCheck = andWhenValue?.values || andWhenValue;
        let condition = this.templateData.parameters.conditions[0].values.find(
          findConditionByNameAndValue.bind(null, andWhenName, andWhenValueToCheck),
        );
        if (
          (andWhenName === 'shippingAddressCountry' ||
            andWhenName === 'shippingAddressState' ||
            andWhenName === 'deliveryMethod' ||
            andWhenName == 'totalOrderWeight' ||
            andWhenName == 'totalOrderValue' ||
            andWhenName == 'itemQuantity' ||
            andWhenName == 'inventoryLocation' ||
            andWhenName == 'boxChosen' ||
            andWhenName == 'isFilteredBy' ||
            andWhenName == 'isNotFilteredBy' ||
            andWhenName == 'orderStatusFix' ||
            andWhenName == 'orderTag' ||
            andWhenName == 'orderSku' ||
            andWhenName == RulesConditions.CreatedOn ||
            andWhenName == RulesConditions.ShipOnDate ||
            andWhenName == RulesConditions.Time ||
            andWhenName == RulesConditions.OrderFulfillmentStatus ||
            andWhenName == RulesConditions.DeliveryStatus) &&
          !condition.value
        ) {
          condition = { ...condition, value: andWhenValue };
        }
        if (!this.selectedConditions[j]) {
          this.selectedConditions[j] = [];
        }
        this.selectedConditions[j].push(condition);
      }
    }
  }

  createComponent() {
    if (!this.componentRef) {
      this.componentRef = this.vcRef.createComponent(RuleTemplateSelectorComponent);
    }
  }

  showTemplateComponent(data: {
    template: string;
    parameters: {
      conditions: Array<any>;
      actions: Array<any>;
    };
    notVerifiedName: string;
  }) {
    this.createComponent();

    this.componentRef.instance.template = data.template;
    this.componentRef.instance.conditions = data.parameters.conditions;
    this.componentRef.instance.actions = data.parameters.actions;
    this.componentRef.instance.selectedRuleParams = this.selectedRuleParams;
    this.componentRef.instance.notVerifiedName = data.notVerifiedName;
  }

  editWorkflow(): void {
    this.componentRef.instance.template = null;
    this.changeDetectorRef.detectChanges();
    if (this.notVerifiedName && this.selectedAction.value === 'notVerified') {
      this.settingsService
        .saveVerifications([{ path: 'notVerifiedName', value: this.notVerifiedName }])
        .subscribe(() => {
          this.editRule();
        });
    } else {
      this.editRule();
    }
  }

  editRule(): void {
    this.store.dispatch(
      AutomationRulesActions.editRule({
        ruleId: this.ruleId,
        selectedConditions: this.selectedConditions,
        selectedAction: this.selectedAction,
        internalName: this.internalName.value,
      }),
    );
  }

  onBackHandler(): void {
    this.onClose();
    this.store.dispatch(
      AutomationRulesActions.goBackToAutomationRulesListDialog({
        step: this.step,
      }),
    );
  }

  isConditionsInsertedProperly(selectedConditions: AutomationRulesActions.SelectedCondition[][]): boolean {
    return selectedConditions?.every((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
      return conditionGroup?.every((selectedCondition: AutomationRulesActions.SelectedCondition) => {
        switch (selectedCondition.name) {
          case RulesConditions.ShippingCountry:
            return (
              selectedCondition.value?.countries?.length &&
              selectedCondition.value?.countries.every((country) => <any>Object.values(CountryCode).includes(country))
            );
          case RulesConditions.ShippingState:
            return selectedCondition.value?.states?.length;
          case RulesConditions.OrderTag:
            return ['includes', 'excludes'].includes(selectedCondition.value?.operator?.value)
              ? selectedCondition.value?.selectedTags?.length
              : selectedCondition.value?.value;
          case RulesConditions.TotalOrderWeight:
          case RulesConditions.ItemQuantity:
          case RulesConditions.TotalOrderValue:
            return !isNaN(selectedCondition.value?.amount) && selectedCondition.value?.amount >= 0;
          case RulesConditions.InventoryLocation:
            return selectedCondition.value?.address;
          case RulesConditions.BoxChosen:
            return selectedCondition.value?.boxesArray?.length;
          case RulesConditions.FilteredBy:
          case RulesConditions.NotFilteredBy:
            return selectedCondition.value?.filtersArray?.length;
          case RulesConditions.OrderSKU:
            return ['includes', 'excludes'].includes(selectedCondition.value?.operator?.value)
              ? selectedCondition.value?.selectedSkus?.length
              : selectedCondition.value?.value;
          case RulesConditions.OrderStatusFix:
            return selectedCondition.value?.statusArray?.length;
          case RulesConditions.DeliveryMethod:
            return selectedCondition.value?.operator && selectedCondition.value?.values?.length;
          case RulesConditions.PaymentStatus:
            return selectedCondition.value?.statuses?.length;
          case RulesConditions.ShipOnDate:
            return selectedCondition.value?.operator && selectedCondition.value?.value.label;
          case RulesConditions.DeliveryStatus:
            return selectedCondition.value?.deliveryStatus?.length;
        }
        return selectedCondition.value;
      });
    });
  }

  isActionInsertedProperly(selectedAction: AutomationRulesActions.SelectedAction): boolean {
    if (!selectedAction) {
      return false;
    }
    switch (selectedAction.name) {
      case RulesActions.ChangeStatusWithDelay:
        return selectedAction.value?.value && selectedAction.value?.delayTime > 1; // Cron minimum is 1 minute
      case RulesActions.SetRecommendedCarrier:
        return (
          selectedAction.value?.shipmentPreferncesFields?.carrier?.length ||
          selectedAction.value?.shipmentPreferncesFields?.maximalTransitTime != null ||
          (selectedAction.value?.shipOn?.shipOn &&
            typeof selectedAction.value?.shipOn?.x === 'number' &&
            (!selectedAction.value?.shipOn?.shipOn?.value?.includes('Before') || selectedAction.value?.shipOn?.before))
        );
      case RulesActions.SetReturnPreferences:
        const preferences: ReturnPreferences = selectedAction.value ?? {};
        return !!(preferences.active && preferences.box?.id && preferences.returnToAddress?.id);
      case RulesActions.Snooze:
        return (
          selectedAction.value?.snoozeBy &&
          selectedAction.value?.x != null &&
          (!selectedAction.value?.snoozeBy?.value?.includes('Before') || selectedAction.value?.before)
        );
      case RulesActions.TeamNote:
        return selectedAction?.value?.message?.length;
      case RulesActions.ColorCoding:
        return !!(selectedAction?.value as string[]);
      case RulesActions.AddTags:
        return selectedAction.value?.length;
      case RulesActions.SplitOrderByStrategy:
        return this.checkSplitByActionValidity(selectedAction?.value);
      case RulesActions.SetWeightToSKU:
        return selectedAction?.value?.weight != null && selectedAction?.value?.sku?.trim()?.length > 0;
    }
    return selectedAction.value;
  }

  isConditionsAndActionsWorkTogether(
    selectedConditions: AutomationRulesActions.SelectedCondition[][],
    selectedAction: AutomationRulesActions.SelectedAction,
  ) {
    if (!selectedAction || !selectedConditions?.length) {
      return false;
    }
    switch (selectedAction.name) {
      case RulesActions.SetWeightToSKU:
        return selectedConditions?.some((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
          return conditionGroup.some((selectedCondition: AutomationRulesActions.SelectedCondition) => {
            switch (selectedCondition.name) {
              case 'orderStatusFix':
                return selectedCondition.value?.statusArray?.some(
                  (status) => status?.value == OrderStatus.FIX_MEASURES,
                );
            }
            return false;
          });
        });
      case 'changeOrderStatus':
      case 'changeOrderStatusWithDelay':
        if (selectedAction.value == OrderStatus.STEP_ASSIGN || selectedAction.value.value == OrderStatus.STEP_ASSIGN) {
          return selectedConditions?.every((conditionGroup: AutomationRulesActions.SelectedCondition[]) => {
            return conditionGroup.every((selectedCondition: AutomationRulesActions.SelectedCondition) => {
              switch (selectedCondition.name) {
                case 'orderStatusFix':
                  return selectedCondition.value?.statusArray?.every((status) => !status?.value.includes('fix'));
              }
              return true;
            });
          });
        }
        break;
    }
    return true;
  }

  onClose(): void {
    this.dialogRef.close();
  }

  checkSplitByActionValidity(actionValue: {
    value: string;
    operator?: string;
    operator2?: string;
    productTags?: RulesTag[];
    freeTextValue?: string;
    tagName?: string;
  }) {
    if (actionValue?.value === 'sku' || actionValue?.value === 'line_item' || actionValue?.value === 'stock_status') {
      return true;
    }

    if (
      actionValue?.value === 'product_tag' &&
      actionValue.productTags?.length &&
      actionValue.operator &&
      ['includes', 'excludes'].includes(actionValue?.operator2)
    ) {
      return true;
    }

    if (
      actionValue?.value === 'product_tag' &&
      actionValue.freeTextValue?.length &&
      actionValue.operator &&
      ['contains', 'notContains'].includes(actionValue.operator2)
    ) {
      return true;
    }

    if (
      actionValue?.value === 'sku_list' &&
      actionValue.operator &&
      ((actionValue.productTags?.length && ['includes', 'excludes'].includes(actionValue?.operator2)) ||
        (actionValue.freeTextValue?.length && ['contains', 'notContains'].includes(actionValue.operator2)))
    ) {
      return true;
    }
    return false;
  }
}
